import {
  RcfaStoppageCode as BaseStoppageCode,
  StoppageGroup,
  RcfaStoppageKind as StoppageKind
} from '@domain/shared'
import {Stack, Tooltip, Typography} from '@mui/material'
import {Variant} from '@mui/material/styles/createTypography'
import {uniqBy} from 'lodash'
import React, {FC} from 'react'

type StoppageCode = Pick<BaseStoppageCode, 'code' | 'text'>
type StoppageCodeLabelProps = {
  groupStoppages?: StoppageGroup[]
  stoppageCode?: StoppageCode
  titleVariant?: Variant
  emptyPlaceholder?: string
}

export const getUniqStoppageCodes = (
  groupStoppages: StoppageGroup[] = [],
  additionalCode?: StoppageCode
): BaseStoppageCode[] =>
  uniqBy(
    groupStoppages
      .filter(
        ({stoppageKind, code}) =>
          stoppageKind !== StoppageKind.PlannedStoppage &&
          code &&
          (!additionalCode?.code || code.code !== additionalCode.code)
      )
      .map(({code}) => code!),
    'code'
  )
export const getStoppageCodeLabel = (
  stoppageCode: StoppageCode,
  groupStoppages?: StoppageGroup[]
) => {
  const moreStoppageCodeCount = getUniqStoppageCodes(groupStoppages, stoppageCode).length
  return stoppageCode?.code
    ? moreStoppageCodeCount
      ? `${stoppageCode.code}${moreStoppageCodeCount ? ` + ${moreStoppageCodeCount}` : ''}`
      : stoppageCode.code
    : ''
}

export const StoppageCodeLabel: FC<StoppageCodeLabelProps> = ({
  stoppageCode,
  groupStoppages,
  titleVariant = 'subtitle2',
  emptyPlaceholder = '-'
}) => {
  const uniqStoppageCodes = getUniqStoppageCodes(groupStoppages, stoppageCode)

  return (
    <Stack>
      <Typography variant={titleVariant} sx={{textTransform: 'capitalize'}}>
        {stoppageCode?.text ?? emptyPlaceholder}
      </Typography>
      {stoppageCode?.code && (
        <Typography variant="caption" sx={{display: 'inline-flex'}}>
          <Tooltip
            arrow
            placement="bottom"
            title={
              uniqStoppageCodes.length ? (
                <Stack gap={0.5}>
                  {uniqStoppageCodes.map(({code, text}) => (
                    <Stack key={`stoppage-code-${code}`}>
                      <Typography variant="body2">{text}</Typography>
                      <Typography variant="caption">{code}</Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : undefined
            }
          >
            <span data-test-id="stoppage-code-label">
              {getStoppageCodeLabel(stoppageCode, groupStoppages)}
            </span>
          </Tooltip>
        </Typography>
      )}
    </Stack>
  )
}
